import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  progressNotePrps: [],
  currentProgressNotePrp: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Progress Notes PRP
export const fetchProgressNotePrps = createAsyncThunk(
  'progressNotePrp/fetchProgressNotePrps',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId ? `/progress-note-prps/${patientId}` : '/progress-note-prps';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Progress Notes PRP'
      );
    }
  }
);

// Fetch a single Progress Note PRP
export const fetchProgressNotePrp = createAsyncThunk(
  'progressNotePrp/fetchProgressNotePrp',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/progress-note-prps/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Progress Note PRP'
      );
    }
  }
);

// Create a new Progress Note PRP
export const createProgressNotePrp = createAsyncThunk(
  'progressNotePrp/createProgressNotePrp',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/progress-note-prps', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create Progress Note PRP'
      );
    }
  }
);

// Update Progress Note PRP
export const updateProgressNotePrp = createAsyncThunk(
  'progressNotePrp/updateProgressNotePrp',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/progress-note-prps/${id}`, formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update Progress Note PRP'
      );
    }
  }
);

// Delete Progress Note PRP
export const deleteProgressNotePrp = createAsyncThunk(
  'progressNotePrp/deleteProgressNotePrp',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/progress-note-prps/${id}`);
      return { id, message: response.data.message };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete Progress Note PRP'
      );
    }
  }
);

// ==================== Slice ====================
const progressNotePrpSlice = createSlice({
  name: 'progressNotePrp',
  initialState,
  reducers: {
    clearCurrentProgressNotePrp: (state) => {
      state.currentProgressNotePrp = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch List
      .addCase(fetchProgressNotePrps.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProgressNotePrps.fulfilled, (state, action) => {
        state.isLoading = false;
        state.progressNotePrps = action.payload;
      })
      .addCase(fetchProgressNotePrps.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Single
      .addCase(fetchProgressNotePrp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProgressNotePrp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProgressNotePrp = action.payload;
      })
      .addCase(fetchProgressNotePrp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createProgressNotePrp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createProgressNotePrp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.progressNotePrps.unshift(action.payload);
      })
      .addCase(createProgressNotePrp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update
      .addCase(updateProgressNotePrp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProgressNotePrp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProgressNotePrp = action.payload;
        const index = state.progressNotePrps.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.progressNotePrps[index] = action.payload;
        }
      })
      .addCase(updateProgressNotePrp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete
      .addCase(deleteProgressNotePrp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProgressNotePrp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.progressNotePrps = state.progressNotePrps.filter((r) => r.id !== action.payload.id);
      })
      .addCase(deleteProgressNotePrp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentProgressNotePrp, clearError } = progressNotePrpSlice.actions;
export default progressNotePrpSlice.reducer;
